// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { createContext, useContext } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import mapValues from 'lodash/mapValues';
import { Density, Mode } from '@cloudscape-design/global-styles';
import { THEME } from '~components/internal/environment';
// `visualRefresh` is kept as a separate flag; this enum covers the additional, mutually
// exclusive themes that map to a single body class.
export var Theme;
(function (Theme) {
    Theme["Default"] = "default";
    Theme["OneTheme"] = "one-theme";
    // Applied through a pregenerated, scoped stylesheet. See pages/app/index.tsx and
    // build-tools/tasks/styles.js.
    Theme["CoreUpdate"] = "core-update";
})(Theme || (Theme = {}));
const themeClassNames = {
    [Theme.Default]: '',
    [Theme.OneTheme]: 'awsui-one-theme',
    [Theme.CoreUpdate]: 'awsui-core-update',
};
export function applyThemeClass(activeTheme) {
    for (const [theme, className] of Object.entries(themeClassNames)) {
        if (className) {
            document.body.classList.toggle(className, theme === activeTheme);
        }
    }
}
const appContextDefaults = {
    mode: Mode.Light,
    pageId: undefined,
    urlParams: {
        density: Density.Comfortable,
        direction: 'ltr',
        visualRefresh: THEME === 'default',
        theme: Theme.Default,
        motionDisabled: false,
        appLayoutWidget: false,
    },
    setMode: () => { },
    setUrlParams: () => { },
};
const AppContext = createContext(appContextDefaults);
export default AppContext;
export function useAppContext() {
    return useContext(AppContext);
}
export function parseQuery(query) {
    const queryParams = { ...appContextDefaults.urlParams };
    const urlParams = new URLSearchParams(query);
    urlParams.forEach((value, key) => (queryParams[key] = value));
    const themeValues = Object.values(Theme);
    return mapValues(queryParams, (value, key) => {
        if (key === 'theme') {
            return themeValues.includes(value) ? value : Theme.Default;
        }
        if (value === 'true' || value === 'false') {
            return value === 'true';
        }
        return value;
    });
}
function formatQuery(params) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === appContextDefaults.urlParams[key]) {
            continue;
        }
        query.set(key, value);
    }
    return query ? `?${query.toString()}` : '';
}
export function AppContextProvider({ children }) {
    var _a, _b, _c, _d;
    const history = useHistory();
    const location = useLocation();
    const matchWithVisualMode = useRouteMatch('/:mode(light|dark)/:pageId*');
    const matchWithoutVisualMode = useRouteMatch('/:pageId*');
    const pageId = (_b = (_a = (matchWithVisualMode !== null && matchWithVisualMode !== void 0 ? matchWithVisualMode : matchWithoutVisualMode)) === null || _a === void 0 ? void 0 : _a.params.pageId) !== null && _b !== void 0 ? _b : undefined;
    const urlParams = parseQuery(location.search);
    const mode = (_d = (_c = matchWithVisualMode === null || matchWithVisualMode === void 0 ? void 0 : matchWithVisualMode.params.mode) !== null && _c !== void 0 ? _c : urlParams.mode) !== null && _d !== void 0 ? _d : Mode.Light;
    function setUrlParams(newParams) {
        const formattedQuery = formatQuery({ ...urlParams, ...newParams });
        if (matchWithVisualMode) {
            const pathname = [matchWithVisualMode.params.mode, pageId].filter(segment => !!segment).join('/') + '/';
            history.replace(`/${pathname}${formatQuery({ ...urlParams, ...newParams })}`);
        }
        else {
            const newUrl = pageId ? `/${pageId}${formattedQuery}` : formattedQuery;
            history.replace(newUrl);
        }
    }
    function updateMode(newMode) {
        if (matchWithVisualMode) {
            const pathname = [newMode, pageId].filter(segment => !!segment).join('/') + '/';
            history.replace('/' + pathname + location.search + location.hash);
        }
        else {
            setUrlParams({ mode: newMode });
        }
    }
    return (React.createElement(AppContext.Provider, { value: { mode, pageId, urlParams, setUrlParams: setUrlParams, setMode: updateMode } }, children));
}
